import { createRef, memo, useCallback, useMemo, useState } from 'react';
import { Box, Text, Input, Button } from '@betty/moonlight';
import { Pressable, TextInput } from 'react-native';
import { MyProfileSectionProps } from './MyProfileSection.types';
import MyProfileReadOnlyField from '../MyProfileReadOnlyField/MyProfileReadOnlyField';

function MyProfileSection({
  fieldData,
  onChange,
  disableEditing,
  onSubmitForm,
  isSubmiting,
  titleLocKey,
  isEditing,
  toggleEdit,
}: MyProfileSectionProps) {
  const [errors, setErrors] = useState<boolean[]>([]);

  const fieldRefs = useMemo(
    () => fieldData.map(() => createRef<TextInput>()),
    [fieldData],
  );

  const firstEditableIndex = useMemo(
    () => fieldData.findIndex((item) => item.isEditable),
    [fieldData],
  );

  const validateForm = useCallback(() => {
    const fieldErrors = fieldData.map((field) =>
      field.regex ? !field.regex?.test(field.value) : false,
    );

    setErrors(fieldErrors);

    if (fieldErrors.some((error) => error)) {
      return;
    } else if (onSubmitForm) {
      onSubmitForm();
    }
  }, [fieldData, onSubmitForm]);

  const onSubmitEditing = useCallback(
    (currentIndex: number, totalLength: number) => {
      if (currentIndex === totalLength - 1) {
        validateForm();
        return;
      }

      fieldRefs[currentIndex + 1].current?.focus();
    },
    [fieldRefs, validateForm],
  );

  return (
    <Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text
          variant="h6"
          color="darkSurfacePrimary"
          locNS="account"
          locKey={titleLocKey}
          paddingVertical="space-800"
        />
        {disableEditing ? null : (
          <Pressable onPress={toggleEdit}>
            <Text
              variant="body-m"
              gradient="darkSurfaceAccent"
              locNS="account"
              locKey="myProfileEditCta"
            />
          </Pressable>
        )}
      </Box>
      <Box flex={1} gap="space-600" width="100%">
        {fieldData.map(
          (
            { locKey, isEditable, value, targetScreen, regexErrorKey },
            index,
          ) =>
            isEditing ? (
              <>
                <Input
                  key={locKey}
                  autoFocus={index === firstEditableIndex}
                  ref={fieldRefs[index]}
                  value={value}
                  placeholderKey={locKey}
                  placeholderNS="account"
                  enterKeyHint={index === fieldData.length - 1 ? 'go' : 'next'}
                  disabled={!isEditable}
                  onChangeText={(value) => onChange && onChange(locKey, value)}
                  onSubmitEditing={() =>
                    onSubmitEditing(index, fieldData.length)
                  }
                />
                {errors[index] && regexErrorKey ? (
                  <Text
                    variant="caption"
                    locNS="account"
                    locKey={regexErrorKey}
                    color="darkSurfaceError"
                  />
                ) : null}
              </>
            ) : (
              <MyProfileReadOnlyField
                key={locKey}
                locKey={locKey}
                value={value}
                targetScreen={targetScreen}
              />
            ),
        )}
      </Box>
      {isEditing ? (
        <Button
          marginTop="space-600"
          locNS="account"
          locKey="myProfileSubmitCta"
          onPress={validateForm}
          loading={isSubmiting}
        />
      ) : null}
    </Box>
  );
}

export default memo(MyProfileSection);
